import { OpenInNewRounded as LinkIcon } from "@mui/icons-material";
import { Box, Link, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGrid,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
} from "@mui/x-data-grid";
import * as logger from "common/logger";
import * as strings from "common/strings";
import { makeChoicesColDef } from "components/common/dataGrid/choicesColumn";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import * as labels from "config.labels";
import { SOV_GRID_COPY_CELLS_ENABLED } from "flags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import { useIsVisible } from "store/hooks/staticDataHooks";

const getDatagridStyle = (theme) => ({
  p: 0,
  "& .warning": {
    backgroundColor: "#ffddc4",
    color: "#712608",
  },
  "& .error": {
    backgroundColor: theme.brand.red[100],
    color: theme.brand.red[800],
  },
  "& .error-cell": {
    backgroundColor: "#de6e6e",
    color: theme.brand.black,
  },
  "& .overridden": {
    color: theme.brand.white,
    backgroundColor: "primary.main",
    fontWeight: "bold",
  },
  "& .risk-low": {
    backgroundColor: theme.brand.green[100],
    color: theme.brand.green[900],
  },
  "& .risk-high": {
    backgroundColor: "#ffddc4",
    color: "#712608",
  },
});

const CHOICE_CLASS_RULES = {
  _ISO_FIRE_PROTECTION_CLASS: {
    ineligibleClass: "error-cell",
    getLoadClass: (load) => {
      const numericLoad = Number(load);
      if (!Number.isFinite(numericLoad)) return null;
      return numericLoad >= 1 ? "risk-high" : "risk-low";
    },
  },
};

const renderCell = ({ params, column, config }) => {
  const override = config.overrides?.[params.id]?.[column.key] ?? null;

  const tooltip = override ? (
    <Box>
      {`Value: ${params.value}`}
      <br />
      {`Original: ${override.original}`}
    </Box>
  ) : (
    params.value
  );

  const sx = {
    textAlign: ["number", "float", "int"].includes(column.type)
      ? "right"
      : "left",
    width: "100%",
    height: "100%",
    ...(column.editable
      ? {
          overflow: "hidden",
          textOverflow: "ellipsis",
        }
      : {}),
  };

  return (
    <Tooltip title={tooltip} followCursor>
      <Box
        sx={sx}
        onContextMenu={(e) => config?.onCellContextMenu?.(params, e)}
      >
        {params.formattedValue}
      </Box>
    </Tooltip>
  );
};

const getEnumChoiceClass = ({ params, column, config }) => {
  const { enumKey, key: columnKey } = column;
  const classRules = CHOICE_CLASS_RULES[columnKey];
  const value = params.value;

  if (!enumKey || !classRules || value == null || value === "__NULL__") {
    return null;
  }

  const choices = config.enum?.enums?.[enumKey]?.choices ?? [];
  const choice = choices.find(
    (choiceItem) => String(choiceItem.key) === String(value)
  );

  if (!choice) return null;
  if (choice.isEligible === false) return classRules.ineligibleClass;

  return classRules.getLoadClass?.(choice.load) ?? null;
};

const getClassName = ({ params, column, config }) => {
  const classes = [];
  const enumChoiceClass = getEnumChoiceClass({ params, column, config });

  const severity = config.validations?.[params.id]?.severity;
  if (severity === "error") {
    classes.unshift("error");
  } else if (severity === "warning") {
    classes.unshift("warning");
  }

  const hasFieldError = !!config.validations?.[params.id]?.fieldErrors?.[
    column.key
  ];
  if (hasFieldError) {
    classes.unshift("error-cell");
  }

  const hasOverride = !!config.overrides?.[params.id]?.[column.key];
  const shouldApplyOverrideClass =
    hasOverride && enumChoiceClass !== "error-cell";
  if (shouldApplyOverrideClass) {
    classes.unshift("overridden");
  }

  if (enumChoiceClass) {
    classes.unshift(enumChoiceClass);
  }

  return classes.join(" ");
};

const makeColumnDef = (config, column) => {
  switch (column.type) {
    case "int":
    case "float":
      const style =
        column.format?.style === "percent"
          ? {
              suffix: "%",
              dp: column.format?.dp ?? 6,
              ...(column.dpMin != null ? { dpMin: column.dpMin } : {}),
              ...(column.dpMax != null ? { dpMax: column.dpMax } : {}),
            }
          : column.format?.style === "currency"
          ? {
              prefix: "$",
              dp: column.format?.dp ?? 0,
              ...(column.dpMin != null ? { dpMin: column.dpMin } : {}),
              ...(column.dpMax != null ? { dpMax: column.dpMax } : {}),
            }
          : {
              dp: column.format?.dp ?? 0,
              ...(column.dpMin != null ? { dpMin: column.dpMin } : {}),
              ...(column.dpMax != null ? { dpMax: column.dpMax } : {}),
            };
      return makePrettyNumberColDef({
        field: column.key,
        headerName: column.name,
        width: column.width ?? 150,
        sortable: true,
        editable: column.editable ?? false,
        ...style,
        cellClassName: (params) => getClassName({ params, column, config }),
        renderCell: (params) => renderCell({ params, column, config }),
      });
    case "enum":
      return makeChoicesColDef({
        field: column.key,
        headerName: column.name,
        choices: config?.enum?.enums?.[column.enumKey]?.choices ?? [],
        width: column.width,
        sortable: true,
        editable: column.editable ?? true,
        cellClassName: (params) => getClassName({ params, column, config }),
        renderCell: (params) => renderCell({ params, column, config }),
        searchNameKey: column.searchNameKey,
      });
    case "link":
      return {
        field: column.key,
        headerName: column.name,
        description: column.description,
        sortable: true,
        editable: false,
        width: column.width,
        cellClassName: (params) => getClassName({ params, column, config }),
        renderCell: (params) =>
          strings.hasValue(params.value) ? (
            <Tooltip title={column.name} followCursor>
              <Link href={params.value} target={"_blank"} rel={"noreferrer"}>
                <Stack
                  sx={{ width: "100%", height: "100%" }}
                  alignItems={"center"}
                  justifyContent={"center"}
                  onContextMenu={(e) => config?.onCellContextMenu?.(params, e)}
                >
                  <LinkIcon />
                </Stack>
              </Link>
            </Tooltip>
          ) : (
            <></>
          ),
      };
    case "string":
    default:
      return {
        type: "text",
        field: column.key,
        headerName: column.name,
        description: column.description,
        sortable: true,
        editable: column.editable,
        width: column.width ?? 150,
        cellClassName: (params) => getClassName({ params, column, config }),
        renderCell: (params) => renderCell({ params, column, config }),
      };
  }
};

const SovGrid = ({
  columnsConfig,
  rows,
  overrides,
  validations,
  enumConfig,
  onRowUpdate,
  onCellValueDelete,
  fillColumn,
  disabled = false,
}) => {
  const theme = useTheme();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const copyToAllLabel = translateLabel(labels.SOV_GRID_COPY_TO_ALL_LABEL, {
    default: "Copy To All Cells",
  });
  const copyToBlankLabel = translateLabel(labels.SOV_GRID_COPY_TO_BLANK_LABEL, {
    default: "Copy To Empty Cells",
  });
  const copyToFilteredLabel = translateLabel(
    labels.SOV_GRID_COPY_TO_FILTERED_LABEL,
    {
      default: "Copy To Filtered Cells",
    }
  );

  const apiRef = useGridApiRef();
  const [contextMenu, setContextMenu] = useState(null);
  const [contextCell, setContextCell] = useState(null);
  const clearOnBackdropRightClick = useRef(null);
  const [columnWidths, setColumnWidths] = useState({});

  const { isVisible: copyMenuVisible } = useIsVisible(
    SOV_GRID_COPY_CELLS_ENABLED
  );
  const isBlank = useCallback(
    (val) => val == null || val === "__NULL__" || val === "",
    []
  );
  const onCellContextMenu = useCallback(
    (newContextCell, e) => {
      if (copyMenuVisible) {
        e.preventDefault();
        e.stopPropagation();
        if (newContextCell?.isEditable && !isBlank(newContextCell.value)) {
          setContextCell(newContextCell);
          setContextMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 6 });
        }
      }
    },
    [copyMenuVisible, isBlank]
  );
  const config = useMemo(
    () => ({
      columns: columnsConfig,
      rows: rows,
      enum: enumConfig,
      validations,
      overrides,
      onCellValueDelete: onCellValueDelete,
      onCellContextMenu,
    }),
    [
      columnsConfig,
      rows,
      enumConfig,
      validations,
      overrides,
      onCellValueDelete,
      onCellContextMenu,
    ]
  );

  const columns = useMemo(
    () =>
      columnsConfig
        ?.map((colConfig) => {
          const userWidth = columnWidths[colConfig.key];
          const configWithWidth = userWidth
            ? { ...colConfig, width: userWidth }
            : colConfig;
          return makeColumnDef(config, configWithWidth);
        })
        ?.map((column) => (disabled ? { ...column, editable: false } : column)),
    [columnsConfig, config, disabled, columnWidths]
  );

  const handleColumnWidthChange = useCallback((params) => {
    setColumnWidths((prev) => ({
      ...prev,
      [params.colDef.field]: params.width,
    }));
  }, []);
  const handleProcessRowUpdate = async (newRow, oldRow) => {
    if (onRowUpdate) {
      await onRowUpdate(newRow, oldRow);
    }
    return newRow;
  };

  const handleCloseMenu = () => {
    setContextMenu(null);
    setContextCell(null);
  };

  const handleCellCopy = (all) => {
    try {
      if (contextCell) {
        const value = contextCell?.value;
        const field = contextCell?.field;
        if (fillColumn) fillColumn(field, value, all);
      }
    } finally {
      handleCloseMenu();
    }
  };
  const handleCopyCellToAll = () => {
    handleCellCopy(true);
  };

  const handleCopyCellToBlanks = () => {
    handleCellCopy(false);
  };

  const handleCopyCellToFiltered = () => {
    try {
      if (contextCell && apiRef.current) {
        const value = contextCell?.value;
        const field = contextCell?.field;

        // Get filtered (visible) row IDs from the DataGrid API
        const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef);
        const visibleRowIds = new Set(filteredRowIds);

        if (fillColumn) {
          fillColumn(field, value, true, visibleRowIds);
        }
      }
    } finally {
      handleCloseMenu();
    }
  };

  // Close the menu on right-click outside the menu
  useEffect(() => {
    if (contextMenu != null) {
      const handleDocContextMenu = (e) => {
        if (!clearOnBackdropRightClick.current) return;
        if (!clearOnBackdropRightClick.current.contains(e.target)) {
          e.stopPropagation();
          e.preventDefault();
          handleCloseMenu();
        }
      };
      document.addEventListener("contextmenu", handleDocContextMenu, true);
      return () =>
        document.removeEventListener("contextmenu", handleDocContextMenu, true);
    }
  }, [contextMenu]);

  return (
    <Box sx={{ width: "100%" }}>
      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        sx={getDatagridStyle(theme)}
        pagination
        pageSizeOptions={[5, 10, 15, 100]}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => logger.error(error)}
        autoHeight
        onColumnWidthChange={handleColumnWidthChange}
        onCellEditStart={(params, event) => {
          if (params?.reason === "deleteKeyDown") {
            onCellValueDelete(params.id, params.field);
            event.defaultMuiPrevented = true;
          }
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              _ADDRESS: false,
            },
          },
          pagination: {
            paginationModel: {
              pageSize: 15,
            },
          },
        }}
      />
      <Menu
        open={contextMenu != null}
        onClose={handleCloseMenu}
        anchorReference={"anchorPosition"}
        anchorPosition={
          contextMenu
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{ paper: { ref: clearOnBackdropRightClick } }}
      >
        <MenuItem onClick={handleCopyCellToAll}>{copyToAllLabel}</MenuItem>
        <MenuItem onClick={handleCopyCellToBlanks}>{copyToBlankLabel}</MenuItem>
        <MenuItem onClick={handleCopyCellToFiltered}>
          {copyToFilteredLabel}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SovGrid;
